import { useState } from 'react';
import packageJson from '../../package.json';
import SmartBuyLogo from '../images/SB.png';
import MasLogo from '../images/MAS.png';
import OTPVerification from './OTPVerification';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { requestOTP, verifyOTP } from '../store/slices/authSlice';
import type { RootState, AppDispatch } from '../store/index';


const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.auth);
  // const { loading, user } = useSelector((state: RootState) => state.auth); // user removed as unused
  const [formData, setFormData] = useState({
    email: '',
    remember: false
  });
  const [showOTP, setShowOTP] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');

  // Version information
  const version = `v${packageJson.version}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    dispatch(requestOTP({email: formData.email})).unwrap().then((response: { success: boolean; message?: string }) => {
      if (response.success) {
        setUserEmail(formData.email);
        setShowOTP(true);
      } else {
        setError(response.message || 'OTP gönderilemedi');
      }
    }).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'OTP gönderilemedi');
    });
  };

  const handleOTPVerify = async (otp: string) => {
    setError(''); // Clear any previous errors
    try {
      const response = await dispatch(verifyOTP({email: userEmail, code: otp})).unwrap();
      if (response.success && response.data) {
        // Store authentication token and user data using UserContext
        
        // Navigate to dashboard page
        navigate('/dashboard');
      } else {
        setError(response.message || 'OTP doğrulanamadı');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OTP doğrulanamadı');
    }

  };

  const handleOTPBack = () => {
    setShowOTP(false);
    setUserEmail('');
    setError('');
  };

  const handleResendOTP = async () => {
    try {
      const response = await dispatch(requestOTP({email: userEmail})).unwrap();
      if (!response.success) {
        setError(response.message || 'OTP tekrar gönderilemedi');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'OTP tekrar gönderilemedi');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  // Show OTP screen if OTP verification is active
  if (showOTP) {
    return (
      <OTPVerification
        email={userEmail}
        onVerify={handleOTPVerify}
        onBack={handleOTPBack}
        onResend={handleResendOTP}
        error={error}
      />
    );
  }

  return (
    <div className="blue-geometric-bg dark:bg-gray-900 min-h-screen font-inter transition-colors duration-300">
      {/* MasLogo in top left */}
      <div className="absolute top-6 left-6 z-20">
        <img 
          src={MasLogo} 
          alt="MAS Logo" 
          className="h-12 w-auto"
        />
      </div>

      {/* Geometric Shapes */}
      <div className="geometric-shape-1"></div>
      <div className="geometric-shape-2"></div>
      <div className="geometric-shape-3"></div>
      <div className="geometric-shape-4"></div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md border border-blue-100 dark:border-gray-700 transition-colors duration-300">
          
          {/* Logo Section */}
          <div className="text-center mb-8 mt-4">
            <div className="flex flex-col items-center justify-center mx-auto">
              <img 
                src={SmartBuyLogo} 
                alt="SmartBuy" 
                className="h-20 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white mt-0 transition-colors">Satın Alma & Talep Yönetimi</h1>
              <p className="text-gray-500 dark:text-gray-400 text-xs transition-colors">E-posta ile güvenli giriş</p>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle text-red-500 mr-2"></i>
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}
            
            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                E-posta Adresi
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-envelope text-gray-400 dark:text-gray-500"></i>
                </div>
                <input 
                  type="text" 
                  id="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder="E-posta adresinizi girin" 
                  required
                />
              </div>
            </div>

            {/* Options Section */}
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="remember" 
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary bg-gray-50 dark:bg-gray-700"
                />
                <label htmlFor="remember" className="ml-2 text-sm text-gray-600 dark:text-gray-400 transition-colors">
                  Beni Hatırla
                </label>
              </div>
            </div>

            {/* Login Button */}
            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-blue-600 focus:ring-4 focus:ring-blue-200 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Doğrulama kodu gönderiliyor...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-2"></i>
                  Doğrulama Kodu Gönder
                </>
              )}
            </button>
          </form>

          {/* Footer Section */}
          <div className="mt-8 text-center space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors">
              © 2025 Satınalma & Talep Yönetim Sistemi. Tüm hakları saklıdır.
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 transition-colors">
              {version}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
